import { useCallback, useEffect, useMemo, useState } from "react";
import { useStore } from "@/stores/useStore";
import { useUpdateUserMutation } from "@/hooks/mutations/useUpdateUserMutation";
import {
  resolveSoundPreferences,
  SoundPreferences,
  TOAST_SOUND_CATEGORIES,
} from "@/lib/toastSound";

/**
 * How long a requested value may outlive the last settled save while waiting
 * for the store to echo it. The store's user echo is slow (it awaits a
 * Firestore query before `set({ user })`), so this is generous; if it expires
 * the UI simply falls back to whatever the stored doc says.
 */
const PENDING_SETTLE_MS = 5000;

const samePreferences = (a: SoundPreferences, b: SoundPreferences) =>
  a.effect === b.effect &&
  TOAST_SOUND_CATEGORIES.every(
    (category) => a.enabled[category] === b.enabled[category]
  );

/**
 * The signed-in user's toast sound settings, and a way to change them.
 *
 * `preferences` is always complete and valid (see `resolveSoundPreferences`).
 * It is the stored user doc, overlaid with any change the user has REQUESTED
 * but the store has not echoed back yet. The overlay matters because the echo
 * is slow: without it, two quick changes would each be built from the same
 * stale stored object and the second whole-object write would silently undo
 * the first.
 *
 * The overlay ("pending") is dropped when:
 * - a save fails (the control reverts to the stored value);
 * - no save is in flight and the stored doc now equals it (the echo arrived);
 * - no save is in flight and the store still hasn't caught up after
 *   `PENDING_SETTLE_MS` (e.g. another device changed the doc), so the UI can
 *   never stay diverged from the stored value.
 * An echo of an earlier change never clears the overlay while a later save is
 * still in flight, and an echo that doesn't match it (a partial catch-up) is
 * ignored until then.
 * @returns {{ preferences: SoundPreferences, save: (next: SoundPreferences) => Promise<void>, canSave: boolean }}
 *   `save` writes the WHOLE normalized object (Firestore `update()` replaces a
 *   map field; a partial object would drop the other keys) and rejects if the
 *   write fails. `canSave` is false until the user doc has loaded.
 */
export function useSoundPreferences(): {
  preferences: SoundPreferences;
  save: (next: SoundPreferences) => Promise<void>;
  canSave: boolean;
} {
  const user = useStore((state) => state.user);
  const { mutateAsync } = useUpdateUserMutation();

  const stored = user?.soundPreferences;
  const storedPreferences = useMemo(
    () => resolveSoundPreferences(stored),
    [stored]
  );

  const [pending, setPending] = useState<SoundPreferences | null>(null);
  const [saving, setSaving] = useState(0);

  useEffect(() => {
    if (!pending || saving > 0) return;
    if (samePreferences(pending, storedPreferences)) {
      setPending(null);
      return;
    }
    const timer = setTimeout(() => setPending(null), PENDING_SETTLE_MS);
    return () => clearTimeout(timer);
  }, [pending, saving, storedPreferences]);

  const userId = user?.id;
  const save = useCallback(
    async (next: SoundPreferences) => {
      if (!userId) return;
      // Re-normalize on the way out so nothing but concrete, known values can
      // ever reach Firestore (no undefined, no stray keys).
      const normalized = resolveSoundPreferences(next);
      setPending(normalized);
      setSaving((count) => count + 1);
      try {
        await mutateAsync({
          userId,
          userData: { soundPreferences: normalized },
        });
      } catch (error) {
        // Revert the control to the stored value; the caller reports the error.
        setPending(null);
        throw error;
      } finally {
        setSaving((count) => count - 1);
      }
    },
    [mutateAsync, userId]
  );

  return {
    preferences: pending ?? storedPreferences,
    save,
    canSave: !!userId,
  };
}
